import { Linkedin, Twitter, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import logoSrc from "@/assets/arqera-logo-optimized.png";
import { analytics } from "@/lib/analytics";

export default function Footer() {
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const socials = [
    { icon: Linkedin, href: "https://www.linkedin.com/company/arqeraai", platform: "LinkedIn" },
    { icon: Twitter, href: "https://twitter.com/arqeraai", platform: "Twitter" },
    { icon: Instagram, href: "https://www.instagram.com/arqeraai/", platform: "Instagram" },
    { icon: Facebook, href: "https://www.facebook.com/arqeraai", platform: "Facebook" },
  ];

  return (
    <footer className="bg-deep-navy border-t border-cyan/20 py-16 px-6" role="contentinfo" aria-label="Site footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-10 md:gap-16 mb-12">
          {/* Column 1: Brand */}
          <div>
            <a href="#top" className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded-md" aria-label="ArqeraAI Home">
              <img src={logoSrc} alt="ArqeraAI Logo" className="h-8 w-8" />
              <span className="text-sm font-bold tracking-wider text-foreground uppercase">
                ARQERA.AI
              </span>
            </a>
            <p className="text-sm text-light-gray mb-6">Architects of Intelligent Systems</p>
            <nav aria-label="Social media links">
              <div className="flex gap-4">
              {socials.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => analytics.clickSocialLink(social.platform)}
                    className="w-8 h-8 rounded-lg bg-cyan/10 flex items-center justify-center text-cyan hover:bg-cyan hover:text-white hover:scale-110 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy"
                    aria-label={`Follow us on ${social.platform}`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
            </nav>
          </div>

          {/* Column 2: Company */}
          <nav aria-label="Company links">
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4 uppercase tracking-wider">
              Company
            </h3>
            <ul className="space-y-2">
              {[
                { label: "Services", href: "#services" },
                { label: "Results", href: "#results" },
                { label: "About", href: "#about" },
                { label: "AI Transformation Audit", href: "/ai-transformation-audit" },
                { label: "Contact", href: "#contact" },
              ].map((link, index) => (
                <li key={`company-${index}`}>
                  {link.href.startsWith("#") ? (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-sm text-medium-gray hover:text-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded"
                      aria-label={`Navigate to ${link.label} section`}
                    >
                      {link.label}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="text-sm text-medium-gray hover:text-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded inline-block"
                      aria-label={`Go to ${link.label} page`}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
          </nav>

          {/* Column 3: Services */}
          <nav aria-label="Services links">
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4 uppercase tracking-wider">
              Services
            </h3>
            <ul className="space-y-2">
              {[
                { label: "AI Growth Engine", href: "#services" },
                { label: "Database Revival", href: "#services" },
                { label: "Systems Audit", href: "#services" },
                { label: "Advanced Solutions", href: "/advanced-solutions" },
              ].map((link, index) => (
                <li key={`${link.href}-${index}`}>
                  {link.href.startsWith("#") ? (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-sm text-medium-gray hover:text-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded"
                      aria-label={`Navigate to ${link.label} section`}
                    >
                      {link.label}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="text-sm text-medium-gray hover:text-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded inline-block"
                      aria-label={`Go to ${link.label} page`}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
          </nav>

          {/* Column 4: Connect */}
          <nav aria-label="Contact and legal links">
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4 uppercase tracking-wider">
              Connect
            </h3>
            <a
              href="mailto:hello@arqeraai.com"
              className="text-sm text-cyan hover:underline mb-4 block focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded"
              aria-label="Email ArqeraAI at hello@arqeraai.com"
            >
              hello@arqeraai.com
            </a>
            <Button
              asChild
              variant="outline"
              className="border border-cyan text-cyan hover:bg-cyan hover:text-white w-full mb-6 text-sm focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy"
            >
              <a
                href="https://calendly.com/arqeraai-strategy-call/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.clickScheduleCall('footer_cta')}
                aria-label="Schedule a strategy call with ArqeraAI on Calendly"
              >
                Schedule Call
              </a>
            </Button>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/privacy" 
                  className="text-sm text-medium-gray hover:text-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded inline-block"
                  aria-label="Read our Privacy Policy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms" 
                  className="text-sm text-medium-gray hover:text-cyan transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded inline-block"
                  aria-label="Read our Terms of Service"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-cyan/20 text-center">
          <p className="text-xs text-medium-gray">
            © 2025 ArqeraAI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
